import { useEffect, useRef } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import {
  analysisStatusApi,
  historyApi,
  briefingApi,
  catalogContextApi,
  catalogsApi,
  destinationsApi,
  findingCountsApi,
  findingsApi,
  invitationsApi,
  profileApi,
  sourcesApi,
  supportAccessApi,
  teamMembersApi,
  type ApplyResult,
  type InviteBody,
  type ListHistoryParams,
  type ListFindingsParams,
  type TableExport,
} from './endpoints';
import { exportFilename, saveBlob } from './fileDownload';
import {
  createChatSession,
  getAgentChatSession,
  type CreateChatSessionResult,
} from './agentChat';
import { boardsApi } from './boards';
import { ApiError } from './client';
import { CellEditUnverifiedError } from './cellEditErrors';
import { useAppStore } from '../store/appStore';
import { useAgentChatStore } from '../store/agentChatStore';
import {
  type AnalysisState,
  type AnalysisStatusResponse,
  type ApplyJobStatus,
  type ApplyProgress,
  type CatalogContext,
  type CatalogContextInput,
  type CatalogResponse,
  type CellEditRequest,
  type CellEditResponse,
  type CreateInvitationResponse,
  type DataSource,
  type Destination,
  type DestinationDetail,
  type ExportFormat,
  type TableQuery,
  type MembershipChange,
  type MembershipSelection,
  type Invitation,
  type PaginatedFindingsResponse,
  type SupportGrant,
  type TeamMember,
  type TeamMembersResponse,
  type FacetGroupSelection,
  type TableFilter,
} from '../types';

export const catalogKeys = {
  all: ['catalogs'] as const,
  list: () => [...catalogKeys.all, 'list'] as const,
  detail: (id: string) => [...catalogKeys.all, 'detail', id] as const,
};

export const destinationKeys = {
  all: ['destinations'] as const,
  list: () => [...destinationKeys.all, 'list'] as const,
  detail: (id: string) => [...destinationKeys.all, 'detail', id] as const,
};

export const sourceKeys = {
  all: ['sources'] as const,
  list: () => [...sourceKeys.all, 'list'] as const,
};

export const findingsKeys = {
  all: ['findings'] as const,
  list: (catalogId: string, params: ListFindingsParams) =>
    [...findingsKeys.all, 'list', catalogId, params] as const,
  // Under `all`, so every finding transition (invalidateFindingSurfaces) refreshes
  // the single-finding fallback and the field options with the list.
  detail: (catalogId: string, findingId: string) =>
    [...findingsKeys.all, 'detail', catalogId, findingId] as const,
  fields: (catalogId: string, params: ListFindingsParams, analysisState?: AnalysisState) =>
    [...findingsKeys.all, 'fields', catalogId, params, analysisState ?? null] as const,
};

export const agentChatKeys = {
  all: ['agentChat'] as const,
  session: (catalogId: string) =>
    [...agentChatKeys.all, 'session', catalogId] as const,
};

export function useAgentChatSession(catalogId: string) {
  return useQuery({
    queryKey: agentChatKeys.session(catalogId),
    queryFn: () => getAgentChatSession(catalogId),
    enabled: !!catalogId,
    // The session is hand-mutated on each stream completion; treat the
    // server-fetched value as authoritative only on mount/catalog-switch.
    staleTime: Infinity,
    gcTime: 5 * 60_000,
  });
}

// Default for the agent briefing — open findings only. Module-level constant
// so the queryKey reference is stable across renders when the caller doesn't
// pass params.
//
// No scope filter: post-SC-2291 every finding from `/findings` is an actionable
// ChangeSet/human_review (the legacy heuristic pattern/gap kinds are gone), so the
// briefing wants them all regardless of breadth. The Attributes surface narrows to
// `scope: ['field','catalog']` via its own params.
//
// perPage is intentionally generous (100): the Agent tab caps its DISPLAY at
// AGENT_TAB_BRIEFING_LIMIT (5) and additionally filters out review-only findings
// client-side, so a small server-side window would risk returning 5 finds that are
// all review-only — leaving the agent tab visually empty even when fixable findings
// exist further down the sort order. 100 matches the BE's FastAPI Query(le=100)
// ceiling and is enough for any demo-scale catalog.
const DEFAULT_BRIEFING_PARAMS: ListFindingsParams = {
  status: ['pending', 'acknowledged'],
  perPage: 100,
};

export function useCatalogs() {
  return useQuery({
    queryKey: catalogKeys.list(),
    queryFn: catalogsApi.list,
  });
}

/**
 * The org's sync destinations (SC-3104) — the channel vocabulary for the whole
 * app: the filter rail's options, the drawer's chips, and (SC-3147/3148) the
 * assign and upload pickers all read from here.
 *
 * Org-level and near-static — destinations change rarely, and the one in-app
 * write (the Connections kill switch, `useSetDestinationEnabled`) invalidates this
 * list itself — so a long `staleTime` is right and a refetch on every mount would
 * be pure waste. `gcTime` outlives it so a catalog switch re-reads cache.
 *
 * `enabled` exists for callers that only sometimes have a channel surface to
 * render — `CatalogUpload`'s inline variant has no picker, so it shouldn't pay
 * for the fetch. Defaults to on; every other caller passes nothing.
 */
export function useDestinations(enabled = true) {
  return useQuery({
    queryKey: destinationKeys.list(),
    queryFn: destinationsApi.list,
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // House convention — surface the failure in ~3s rather than after react-query's
    // default 3 retries. A channel rail that can't load should say so quickly.
    retry: 1,
  });
}

/**
 * Index destinations by id, for resolving an entity's `destination_ids` into
 * labels. Returns a `Map` so a miss is explicit at the call site rather than
 * `undefined` from an object index.
 *
 * A miss is REACHABLE, not defensive: a destination can be deleted after rows
 * were tagged, and one whose `catalog_ids` no longer covers this catalog leaves
 * its id on rows that no longer reach it. Callers must render the unknown case
 * as an unrecognised channel — never blank, and never by failing the read.
 */
export function destinationsById(list: Destination[] | undefined): Map<string, Destination> {
  return new Map((list ?? []).map((d) => [d.id, d]));
}

/**
 * Each destination's FULL record (SC-3122) — the only read that carries
 * `catalog_ids` and `lifecycle`. One query per id rather than one aggregate, so a
 * row whose detail fails renders its own error while its siblings still load, and
 * so the kill switch can write one row's cache without refetching the rest.
 *
 * N requests for N destinations is fine at an org's handful. `enabled` must be
 * false below `manage_destinations`: the route 403s, and a 403 is not an error to
 * show a member who was never going to see this.
 */
export function useDestinationDetails(ids: string[], enabled: boolean) {
  return useQueries({
    queries: ids.map((id) => ({ ...destinationDetailQuery(id), enabled })),
  });
}

/** One destination's full record — the Connections detail view (SC-3122). Same
 *  key and cache as `useDestinationDetails`, so opening a row from the table
 *  reads what the table already fetched. */
export function useDestinationDetail(id: string, enabled: boolean) {
  return useQuery({ ...destinationDetailQuery(id), enabled });
}

function destinationDetailQuery(id: string) {
  return {
    queryKey: destinationKeys.detail(id),
    queryFn: () => destinationsApi.get(id),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    // One retry, except on a 404: another org's id and a deleted destination both
    // 404, and asking again cannot change that answer.
    retry: (failureCount: number, error: Error) =>
      !(error instanceof ApiError && error.status === 404) && failureCount < 1,
  };
}

/**
 * The org's inbound sources (SC-3122). `view_sources`-gated, so the caller passes
 * `enabled` from the permission check rather than letting a member eat a 403.
 * Read-only over the API, so nothing in-app changes it: same long cache as the
 * destinations list.
 */
export function useSources(enabled: boolean) {
  return useQuery<DataSource[]>({
    queryKey: sourceKeys.list(),
    queryFn: sourcesApi.list,
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

export interface SetDestinationEnabledVars {
  id: string;
  enabled: boolean;
}

/**
 * The destination kill switch (SC-3122). Deliberately NOT optimistic: this changes
 * what leaves the building, so the row shows the new state only once the server
 * has it — a toggle that flips and then flips back is worse than a short wait.
 *
 * On success the returned record replaces that row's detail cache, and the ungated
 * list is invalidated too — the channel chips and pickers elsewhere render a
 * disabled destination as paused, so they read `enabled` from there. Returned so
 * the mutation stays pending until the refetch lands.
 *
 * On error the detail is refetched: the likeliest refusal is a 422 because the
 * destination is now `deleting`, and the row should say so rather than keep
 * offering a toggle the server will refuse again.
 */
export function useSetDestinationEnabled() {
  const qc = useQueryClient();
  return useMutation<DestinationDetail, Error, SetDestinationEnabledVars>({
    mutationFn: ({ id, enabled }) => destinationsApi.setEnabled(id, enabled),
    onSuccess: (updated) => {
      qc.setQueryData(destinationKeys.detail(updated.id), updated);
      return qc.invalidateQueries({ queryKey: destinationKeys.list() });
    },
    onError: (_err, { id }) =>
      qc.invalidateQueries({ queryKey: destinationKeys.detail(id) }),
  });
}

/** One membership gesture: which channel, and which rows. */
export interface ChannelMembershipVars {
  slug: string;
  selection: MembershipSelection;
}

/**
 * Everything a membership change invalidates, in one place (SC-3147).
 *
 * All three affected reads — the entity detail (`destination_ids` just moved), the
 * table page, and the facet counts — key under `catalogKeys.detail(catalogId)`, so
 * one prefix invalidation covers them. Spelling that out matters because the table
 * and facets look unrelated to a membership write until you notice that a
 * `destination` filter may be active: rows can have just left the view the user is
 * looking at, and the facet counts are computed over that same filtered set.
 *
 * NOT invalidated: the destinations registry itself. Membership lives on entities;
 * the channel list is unchanged by a gesture that moves rows into or out of one.
 */
export function invalidateChannelMembership(
  qc: ReturnType<typeof useQueryClient>,
  catalogId: string,
) {
  // RETURNS the promise, and the callers await it by returning it from their own
  // `onSuccess`. react-query settles a mutation only once an `onSuccess` promise
  // resolves, so this is what keeps the chips disabled until the refetched
  // membership is actually in cache. Dropping it re-enables a control on data the
  // app already knows is stale — right after the write that changed it, which is
  // the one moment "did that work?" is expensive to get wrong.
  return qc.invalidateQueries({ queryKey: catalogKeys.detail(catalogId) });
}

/**
 * Add rows to a channel's assortment. The selection is built by the caller — from
 * the table's live filters, or a single entity's identifier for the drawer toggle.
 *
 * Deliberately NOT optimistic. An optimistic membership chip would have to guess
 * `matched`/`changed`, and those two numbers are the whole result: `matched: 0`
 * means the selection found nothing (a user mistake worth surfacing) while
 * `changed: 0` with matches is a legitimate no-op. Neither is knowable client-side
 * before the server answers, so there is nothing honest to render ahead of it.
 */
export function useAddChannelMembers(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<MembershipChange, Error, ChannelMembershipVars>({
    mutationFn: ({ slug, selection }) =>
      destinationsApi.addMembers(catalogId, slug, selection),
    onSuccess: () => invalidateChannelMembership(qc, catalogId),
  });
}

/**
 * Remove rows from a channel's assortment. Same contract as the add, and the same
 * refusal to guess a selection — the backend enforces it in both directions for a
 * reason it states plainly: removal is the direction that loses data, and a bare
 * unassign normalising into "everything" would silently empty a channel's whole
 * assortment, which the next reconcile would read as a deliberate descope.
 */
export function useRemoveChannelMembers(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<MembershipChange, Error, ChannelMembershipVars>({
    mutationFn: ({ slug, selection }) =>
      destinationsApi.removeMembers(catalogId, slug, selection),
    onSuccess: () => invalidateChannelMembership(qc, catalogId),
  });
}

/**
 * Does this cell-edit failure leave the cell's on-screen value in doubt? Then the
 * caller must refetch before the user may try again:
 *  - 409: someone changed the cell since it loaded. Nothing was written, and the
 *    retry has to be grounded on the value that is there NOW.
 *  - 404: the row is gone; the table should stop showing it.
 *  - no HTTP status at all (a network failure), or a 5xx: the edit may or may
 *    not have landed. The refetch shows what actually happened; a blind resend of
 *    one that did would only 409 under strict grounding.
 * A 422/401/403 changed nothing and says nothing about the cell, so no refetch.
 */
export function cellEditNeedsRefetch(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 409 || error.status === 404 || error.status >= 500;
}

/** Does this failure's meaning depend on what the cell holds now? (404 doesn't.) */
function cellEditNeedsVerifiedRead(error: unknown): boolean {
  return cellEditNeedsRefetch(error) && !(error instanceof ApiError && error.status === 404);
}

/**
 * SC-3466 — whether a write to one cell of `field` can move the counts a cached
 * facets entry holds, read off its query key (`facetsQueryKey`). A cell edit used
 * to refetch every facet query under the catalog; with expanded rail rows
 * cached one per request, most of them count an unrelated column under filters
 * the edit can't touch.
 *
 * Moves when ANY of: the entry carries the Completeness rollup (a blank ↔ filled
 * edit shifts a row's band); it's the default rail (`fields` null — unknown
 * groups); it counts `field` itself; or a filter can re-admit or drop the edited
 * row — one on `field`, the catalog-wide text search, completeness, or a finding
 * filter. Filters on OTHER columns, pinned ids and channel membership can't change
 * with this cell, so an entry built only from those keeps its counts.
 */
export function facetsAffectedByCellEdit(queryKey: readonly unknown[], field: string): boolean {
  const filters = queryKey[queryKey.length - 2] as TableFilter[] | undefined;
  const selection = queryKey[queryKey.length - 1] as FacetGroupSelection | undefined;
  // Not the shape we wrote: refresh rather than guess.
  if (!Array.isArray(filters) || !selection || typeof selection !== 'object') return true;
  if (selection.includeBand || selection.fields === null || selection.fields.includes(field)) {
    return true;
  }
  return filters.some((f) => {
    if (f.type === 'field_value') return f.field === field;
    return f.type !== 'identifier' && f.type !== 'destination';
  });
}

/**
 * One person editing one cell (SC-3113 over SC-3106's route). Not optimistic: the
 * server is the only judge of whether `before` still holds, so there is nothing
 * honest to render ahead of its answer.
 *
 * Every refresh is awaited before the mutation settles (the
 * `invalidateChannelMembership` pattern). That keeps the editor busy until the
 * refetched value is in cache, so a retry after a 409 is grounded on the fresh
 * value rather than the one that just failed.
 *
 * `catalogKeys.detail` covers the table page, the drawer's entity read, and the
 * facet counts (a value change moves facet buckets). The findings surfaces are
 * refreshed too: an edit can overtake an open suggestion on the same cell — and
 * that holds on the uncertain path as well, since the editor may conclude from the
 * refetch that the edit landed.
 *
 * On a 409 or an unconfirmed save the editor decides what happened FROM the
 * refetched cell, so that read must actually succeed: a failed refetch of the
 * table page or the drawer's entity becomes `CellEditUnverifiedError` instead of a
 * stale value silently standing in for a fresh one.
 */
export function useCellEdit(catalogId: string) {
  const qc = useQueryClient();
  // The reads a cell's value comes from: the table page and the drawer's entity,
  // by SKU or by row id (`[...catalogKeys.detail(id), 'table' | 'entity' | 'row', …]`,
  // the last SC-3517's). Only THESE are verified on the uncertain path — a failed
  // facet-count refresh says nothing about the cell, and must not turn a readable
  // 409 into "couldn't check".
  const isCellRead = (query: Query) => {
    const segment = query.queryKey[catalogKeys.detail(catalogId).length];
    return segment === 'table' || segment === 'entity' || segment === 'row';
  };
  // SC-3466 — the other catalog reads, minus what one cell can't move: the rail's
  // layout (pins + group list) never depends on a value, and a facet entry is
  // refreshed only when `facetsAffectedByCellEdit` says the edited column can
  // shift its counts — so expanded rail rows on unrelated columns keep
  // theirs instead of the whole rail refetching per edit.
  // `field` undefined ⇒ the ROW changed, not one cell (a 404: it was deleted), so
  // every facet count it fed is stale.
  const isAffectedNonCellRead = (query: Query, field: string | undefined) => {
    const segment = query.queryKey[catalogKeys.detail(catalogId).length];
    if (segment === 'facet-fields') return false;
    if (segment === 'facets') {
      return field === undefined || facetsAffectedByCellEdit(query.queryKey, field);
    }
    return !isCellRead(query);
  };
  const refreshSurfaces = (field: string | undefined, verifyCellReads = false) =>
    Promise.all([
      qc.invalidateQueries(
        { queryKey: catalogKeys.detail(catalogId), predicate: isCellRead },
        { throwOnError: verifyCellReads },
      ),
      qc.invalidateQueries({
        queryKey: catalogKeys.detail(catalogId),
        predicate: (q) => isAffectedNonCellRead(q, field),
      }),
      qc.invalidateQueries({ queryKey: findingsKeys.all }),
      qc.invalidateQueries({ queryKey: findingCountsKeys.all }),
    ]);
  return useMutation<CellEditResponse, Error, CellEditRequest>({
    mutationFn: async (body) => {
      try {
        return await catalogsApi.cellEdit(catalogId, body);
      } catch (error) {
        if (cellEditNeedsVerifiedRead(error)) {
          try {
            await refreshSurfaces(body.field, true);
          } catch {
            throw new CellEditUnverifiedError(error);
          }
        } else if (cellEditNeedsRefetch(error)) {
          // 404: the row is gone; refresh so the table stops showing it.
          await refreshSurfaces(undefined);
        }
        throw error;
      }
    },
    onSuccess: (_data, body) => refreshSurfaces(body.field),
  });
}

export interface ExportTableVars {
  query: TableQuery;
  format: ExportFormat;
  /** Names the file when the response carries no usable `Content-Disposition`. */
  catalogName: string;
}

export type ExportedFile = Omit<TableExport, 'blob' | 'contentDisposition'> & {
  filename: string;
};

/**
 * Export the grid's current view as a file and save it (SC-3149). The save
 * happens INSIDE `mutationFn`, not in a per-call `onSuccess`: react-query drops
 * `mutate()` callbacks once the calling component unmounts, and a user who
 * clicks Export and then switches tab should still get their file.
 *
 * No cache to invalidate — an export reads, it changes nothing.
 */
export function useExportTable(catalogId: string) {
  return useMutation<ExportedFile, Error, ExportTableVars>({
    mutationFn: async ({ query, format, catalogName }) => {
      const { blob, contentDisposition, ...meta } = await catalogsApi.export(
        catalogId,
        query,
        format,
      );
      const filename = exportFilename(contentDisposition, catalogName, format);
      saveBlob(blob, filename);
      return { ...meta, filename };
    },
  });
}

export function useCatalogEntities(id: string, page = 1, perPage = 25) {
  return useQuery({
    queryKey: [...catalogKeys.detail(id), 'entities', page, perPage] as const,
    queryFn: () => catalogsApi.entities(id, page, perPage),
    enabled: !!id,
    // Hold the prior page on screen during refetch so paging doesn't flash a spinner.
    placeholderData: (prev) => prev,
    // Default react-query retry is 3 with exponential backoff (~30s before the
    // error surfaces). For pagination clicks we want the error banner to
    // appear within ~3s when the backend is unreachable, so the user sees
    // *something* instead of staring at greyed-out buttons.
    retry: 1,
    // Entity pages are stable enough that a 30s freshness window is reasonable.
    // Avoids redundant refetches when bouncing between pages — matters
    // specifically for the post-error "snap back to last-good page" path in
    // EntitiesPage, where re-observing a cached page would otherwise trigger
    // a wasteful re-fetch.
    staleTime: 30_000,
    // Keep the cache alive for 30s after the last observer unmounts, so
    // navigating away and back (page 1 → 2 → 1) hits cache instead of
    // re-fetching. The client-default gcTime in tests is 0 (eager eviction
    // between unrelated tests); this per-query override restores realistic
    // navigation cache behavior.
    gcTime: 30_000,
  });
}

// Staged polling for findings — analysis takes ~5-7 min (Pass 1 LLM + Pass 2
// structurer LLM). Constant fast polling would burn requests pointlessly in
// the first few minutes. The stages back off when no result is likely, and
// ramp up inside the expected-completion window.
export interface PollStage {
  /** This stage applies while elapsed time is below this threshold. */
  untilMs: number;
  /** Refetch interval to use within this stage. */
  intervalMs: number;
}

export const FINDINGS_POLL_STAGES: readonly PollStage[] = [
  { untilMs: 3 * 60_000, intervalMs: 60_000 },
  { untilMs: 5 * 60_000, intervalMs: 30_000 },
  { untilMs: 15 * 60_000, intervalMs: 10_000 },
];

/**
 * Returns the appropriate interval for the elapsed time, or `false` once
 * elapsed time has exceeded the final stage threshold (the consumer should
 * stop polling and surface a "stuck/failed" state).
 */
export function pickStageInterval(
  elapsedMs: number,
  stages: readonly PollStage[] = FINDINGS_POLL_STAGES,
): number | false {
  for (const stage of stages) {
    if (elapsedMs < stage.untilMs) return stage.intervalMs;
  }
  return false;
}

interface FindingsPollableState {
  data: PaginatedFindingsResponse | undefined;
  error: unknown;
  startedAtMs: number;
  nowMs: number;
  /** Latest analysis-status state, if known. `complete`/`failed` are terminal
   *  and stop polling regardless of items count — without this, clean catalogs
   *  (analysis done with 0 findings) would poll for the full 15-min window. */
  analysisState?: AnalysisState;
}

/**
 * Pure decision function — extracted so unit tests can exercise the empty /
 * data-present / error / stage-boundary cases without driving react-query's
 * scheduler.
 */
export function shouldPollFindings(state: FindingsPollableState): number | false {
  // Analysis pipeline reached a terminal state — no point polling further.
  // Covers the "clean catalog" case (complete + 0 findings) and the failed
  // case (where the pipeline won't produce more results without re-upload).
  if (state.analysisState === 'complete' || state.analysisState === 'failed') return false;
  // Analysis complete — items present, no need to keep polling.
  if (state.data && state.data.items.length > 0) return false;
  // Hard error (network / auth / 5xx) — let the consumer's error UI take over.
  if (state.error) return false;
  return pickStageInterval(state.nowMs - state.startedAtMs);
}

export function useCatalogFindings(
  catalogId: string,
  params: ListFindingsParams = DEFAULT_BRIEFING_PARAMS,
  analysisState?: AnalysisState,
) {
  // useRef<number | null>(null) + lazy init via the effect below — React's
  // purity rule disallows Date.now() during render, so we seed the timer on
  // mount instead.
  const startedAtRef = useRef<number | null>(null);
  useEffect(() => {
    // Seed on mount and reset whenever the catalog id changes (so polling
    // restarts when the user navigates to a different catalog).
    startedAtRef.current = Date.now();
  }, [catalogId]);

  return useQuery({
    queryKey: findingsKeys.list(catalogId, params),
    queryFn: () => findingsApi.list(catalogId, params),
    enabled: !!catalogId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    // Paging / re-sorting / re-filtering keeps the current rows on screen until the
    // next page lands (SC-3532), instead of flashing the loading spinner each time —
    // but only within ONE catalog: a catalog switch must never show the last one's rows.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === catalogId ? prev : undefined),
    refetchInterval: (q) =>
      shouldPollFindings({
        data: q.state.data,
        error: q.state.error,
        // If the effect hasn't fired yet (first render before mount), treat
        // elapsed=0 by anchoring to now — first stage interval applies.
        startedAtMs: startedAtRef.current ?? Date.now(),
        nowMs: Date.now(),
        analysisState,
      }),
  });
}

// How often the Field filter's options re-read while analysis runs (they follow the
// findings list, which polls on its own staged schedule).
const FIELD_OPTIONS_POLL_MS = 15_000;

/** One finding by id (SC-3532) — the overlay's fallback when `?finding=<id>` is not on
 *  the loaded page. Pass `null` to skip (the finding IS on the page). */
export function useFinding(catalogId: string, findingId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: findingsKeys.detail(catalogId, findingId ?? ''),
    queryFn: () => findingsApi.get(catalogId, findingId as string),
    enabled: !!catalogId && !!findingId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    // A finding that just moved off the loaded page (a refetch re-ordered the list) was
    // on a cached page a moment ago: show that copy while the fetch runs, so its open
    // overlay doesn't unmount and remount. PLACEHOLDER, not initialData — it is never
    // cached as fresh, so the fetch always runs and a finding resolved out of the view
    // still closes once the server says so.
    placeholderData: () => {
      for (const [, page] of qc.getQueriesData<PaginatedFindingsResponse>({
        queryKey: [...findingsKeys.all, 'list', catalogId],
      })) {
        const hit = page?.items.find((f) => f.id === findingId);
        if (hit) return hit;
      }
      return undefined;
    },
  });
}

/** The Findings field filter's options (SC-3533): fields with ≥1 finding under the
 *  OTHER active filters, with counts. `params` should omit field/sort/paging — the
 *  key then stays put while the person pages or sorts. */
export function useFindingFields(
  catalogId: string,
  params: ListFindingsParams,
  analysisState?: AnalysisState,
) {
  return useQuery({
    // The analysis state rides in the key, so a run FINISHING is a new query — the
    // options re-read once the findings exist, instead of a list cached empty while
    // analysis was still running (Greptile P2 on #196).
    queryKey: findingsKeys.fields(catalogId, params, analysisState),
    queryFn: () => findingsApi.fields(catalogId, params),
    enabled: !!catalogId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    // While a run is minting findings, keep the options moving with the list.
    refetchInterval: analysisState === 'running' ? FIELD_OPTIONS_POLL_MS : false,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === catalogId ? prev : undefined),
  });
}

/** A finding action's variables — the finding plus, for apply, an optional
 *  single-entity scope (SC-2384/SC-2418): `entityId` set ⇒ apply just that
 *  entity's cell (the drawer's per-row Fix); omitted ⇒ apply the whole finding. */
export interface ApplyFindingVars {
  findingId: string;
  entityId?: string;
}

// Invalidate every surface a finding transition can touch: the findings list +
// counts (SC-2231/2271 surfaces), AND `catalogKeys.detail` — which covers the
// entity-detail overlay the drawer renders from + the table grid overlay +
// the entities list. The drawer's findings come from the entity-detail query,
// NOT useCatalogFindings, so without this the drawer wouldn't refresh (SC-2418).
export function invalidateFindingSurfaces(qc: ReturnType<typeof useQueryClient>, catalogId: string) {
  qc.invalidateQueries({ queryKey: findingsKeys.all });
  qc.invalidateQueries({ queryKey: findingCountsKeys.all });
  qc.invalidateQueries({ queryKey: catalogKeys.detail(catalogId) });
}

// Applies a single finding (SC-2231 reviewer + SC-2418 drawer). The chat reviewer
// reflects the applied state locally — the get_findings tool result it renders
// from is frozen history — but the findings list + counts + overlays on other
// surfaces must refetch, so invalidate them on success.
export function useApplyFinding(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<ApplyResult, Error, ApplyFindingVars>({
    mutationFn: ({ findingId, entityId }) => findingsApi.apply(catalogId, findingId, entityId),
    onSuccess: (result, { findingId }) => {
      // SC-3476 — a new job makes any cached status for this finding the PREVIOUS
      // job's (the poll reads the finding's latest job, and an earlier one's terminal
      // read can sit in the cache for gcTime — longer while a chat card observes it).
      // Left in place, the tracker's first render reads that old `applied`/`failed`
      // as THIS job's end: a receipt, or an old error, while the new job is queued.
      if (result.kind === 'queued') {
        void qc.resetQueries({ queryKey: applyProgressKeys.detail(catalogId, findingId), exact: true });
      }
      // A synchronous apply (`204` — the per-row Fix, or a whole finding before
      // the BE 202 flip) is terminal here, so refresh the surfaces now. A queued
      // (`202`) whole-finding apply is NOT done yet: the finding resolves when the
      // poll reaches a terminal `applied`, and THAT is where the surfaces refresh
      // (the drawer's apply state machine calls invalidateFindingSurfaces on
      // terminal). Invalidating here would prematurely drop the finding from the
      // queue mid-apply (SC-2580).
      if (result.kind === 'applied') invalidateFindingSurfaces(qc, catalogId);
    },
  });
}

export const applyProgressKeys = {
  all: ['apply-progress'] as const,
  detail: (catalogId: string, findingId: string) =>
    [...applyProgressKeys.all, catalogId, findingId] as const,
};

// SC-2580 — an async whole-finding apply is terminal once the BE reports
// `applied` or `failed`; `queued`/`applying` are the live states, `null` = no job
// has ever existed for this finding. Exported so both the poll cadence and the
// drawer state machine agree on what "done" means.
export function isApplyTerminal(status: ApplyJobStatus | null): boolean {
  return status === 'applied' || status === 'failed';
}

const APPLY_PROGRESS_POLL_INTERVAL_MS = 1750;

// Pure poll-cadence decision (extracted for unit tests, mirroring
// shouldPollProfile): keep polling every ~1.75s while a job is live, stop on a
// terminal status or no-job. NOTE the stop signal is `status`, NOT
// `fully_applied` — a finding with no value cells (human_review / structural
// SchemaMutation) reports `fully_applied: false` forever even once it completes.
export function applyPollInterval(state: { data?: ApplyProgress }): number | false {
  const status = state.data?.status ?? null;
  return status === 'queued' || status === 'applying'
    ? APPLY_PROGRESS_POLL_INTERVAL_MS
    : false;
}

// Polls `GET …/apply` for an async whole-finding apply (SC-2580). Gate it with
// `enabled` so it only runs while a job is actually in flight; it self-halts on a
// terminal status via `applyPollInterval`. The single live read is intentional
// (no staleTime) so progress is fresh on every observer mount.
export function useApplyProgress(
  catalogId: string,
  findingId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: applyProgressKeys.detail(catalogId, findingId),
    queryFn: () => findingsApi.applyProgress(catalogId, findingId),
    enabled: enabled && !!catalogId && !!findingId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchInterval: (q) => applyPollInterval(q.state),
  });
}

// Acknowledges a single finding (SC-2418) — "reviewed, no change". Same cache
// story as apply/dismiss: invalidate the findings + counts + overlays.
export function useAcknowledgeFinding(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (findingId) => findingsApi.acknowledge(catalogId, findingId),
    onSuccess: () => invalidateFindingSurfaces(qc, catalogId),
  });
}

// Dismisses a single finding (SC-2271). Same cache story as useApplyFinding:
// the chat reviewer reflects the dismissed state locally (frozen get_findings
// history), but the findings list + counts + overlays on other surfaces must
// refetch, so invalidate them on success.
export function useDismissFinding(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (findingId) => findingsApi.dismiss(catalogId, findingId),
    onSuccess: () => invalidateFindingSurfaces(qc, catalogId),
  });
}

// SC-3424 History feed. `staleTime: 0` so opening History right after an apply or a
// cell edit refetches instead of showing the cached pre-change page. `placeholderData: (prev) => prev` smooths pagination
// transitions (project convention, see useCatalogEntities).
export const historyKeys = {
  all: ['history'] as const,
  list: (catalogId: string, params: ListHistoryParams) =>
    [...historyKeys.all, 'list', catalogId, params] as const,
};

export function useHistory(
  catalogId: string,
  params: ListHistoryParams = {},
) {
  return useQuery({
    queryKey: historyKeys.list(catalogId, params),
    queryFn: () => historyApi.list(catalogId, params),
    enabled: !!catalogId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    placeholderData: (prev) => prev,
    retry: 1,
  });
}

export const profileKeys = {
  all: ['catalog-profile'] as const,
  detail: (catalogId: string) => [...profileKeys.all, 'detail', catalogId] as const,
};

export const PROFILE_POLL_INTERVAL_MS = 3_000;
// Cap polling at ~90s of "still generating" to avoid hammering the BE forever
// if auto-gen genuinely failed (LLM down, transient back-end error). After
// this many attempts, the page surfaces a "generation timed out" state.
export const PROFILE_MAX_POLL_ATTEMPTS = 30;

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

interface PollableQueryState {
  data: unknown;
  error: unknown;
  /** Cumulative count of error settlements. Use this and NOT
   * `fetchFailureCount` — react-query resets `fetchFailureCount` to 0 at the
   * start of every fetch, so it never grows past 1 with `retry: false` and
   * was making the cap unreachable. `errorUpdateCount` is the stable counter. */
  errorUpdateCount: number;
}

/**
 * Pure decision function for the profile poll cadence — extracted so unit
 * tests can exercise the boundary cases (404 + under cap, 404 + at cap, data
 * present, non-404 error) without driving react-query's internal scheduler.
 */
export function shouldPollProfile(state: PollableQueryState): number | false {
  if (state.data) return false;
  if (isNotFound(state.error) && state.errorUpdateCount < PROFILE_MAX_POLL_ATTEMPTS) {
    return PROFILE_POLL_INTERVAL_MS;
  }
  return false;
}

export function useCatalogProfile(catalogId: string) {
  return useQuery({
    queryKey: profileKeys.detail(catalogId),
    queryFn: () => profileApi.get(catalogId),
    enabled: !!catalogId,
    staleTime: 60_000,
    // 404 = "not yet generated" (auto-gen background task hasn't finished, or
    // catalog truly doesn't exist for this org). Don't retry-storm; let the
    // refetchInterval below poll. Retry once on other errors so transient
    // network blips don't dump the user into the error state immediately.
    retry: (failureCount, error) => {
      if (isNotFound(error)) return false;
      return failureCount < 1;
    },
    refetchInterval: (query) => shouldPollProfile(query.state),
  });
}

export const attributesKeys = {
  all: ['catalog-attributes'] as const,
  detail: (catalogId: string) => [...attributesKeys.all, 'detail', catalogId] as const,
};

// SC-2381 attributes view (drives the SC-2392 drawer field grouping). A
// projection of the same profile doc as useCatalogProfile, so it 404s on the
// SAME "not yet generated" window. Rather than run a second 404-poller alongside
// the profile's, the caller gates this on the profile being present (`enabled`),
// so it fires exactly once the profile lands — no 404-storm, and the drawer
// degrades to its flat (ungrouped) layout until then. No poll: if the profile
// regenerates, react-query refetches this on the next mount/staleness; grouping
// is purely additive so a brief staleness is invisible.
export function useCatalogAttributes(
  catalogId: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: attributesKeys.detail(catalogId),
    queryFn: () => profileApi.attributes(catalogId),
    enabled: !!catalogId && enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    // 404 = profile not generated yet / cross-tenant — don't retry. Retry once
    // on transient errors. The drawer renders flat on any failure, so this never
    // surfaces an error state of its own.
    retry: (failureCount, error) => {
      if (isNotFound(error)) return false;
      return failureCount < 1;
    },
  });
}

// SC-2445 — per-catalog "Catalog Info" context. User-authored (not a pipeline
// output), so no poll. A 404 means "not captured yet" → the tab renders a clean
// empty form rather than an error, so don't retry-storm on it; other errors get
// one retry. The save mutation writes the BE's authoritative response straight
// into the cache so the tab reflects it without a refetch round-trip.
export const catalogContextKeys = {
  all: ['catalog-context'] as const,
  detail: (catalogId: string) => [...catalogContextKeys.all, 'detail', catalogId] as const,
};

export function useCatalogContext(catalogId: string) {
  return useQuery({
    queryKey: catalogContextKeys.detail(catalogId),
    queryFn: () => catalogContextApi.get(catalogId),
    enabled: !!catalogId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: (failureCount, error) => {
      if (isNotFound(error)) return false;
      return failureCount < 1;
    },
  });
}

export function useUpdateCatalogContext(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<CatalogContext, Error, CatalogContextInput>({
    mutationFn: (body) => catalogContextApi.update(catalogId, body),
    onSuccess: (data) => {
      qc.setQueryData(catalogContextKeys.detail(catalogId), data);
    },
  });
}

// Briefing endpoint (SC-2242) — drives the narrative / recommended-action /
// catalog+user meta surface previously bundled into useDashboard. The BE caches
// the briefing per the living-briefing model; FE forces regen with `?refresh`,
// which is now wired only to explicit new-chat actions (SC-2251). The earlier
// 30-min-stale-mount heuristic was retired once chat sessions became real.
// `briefingKeys` is exported so finding-resolution mutations can invalidate.
export const briefingKeys = {
  all: ['briefing'] as const,
  detail: (catalogId: string, refresh: boolean) =>
    [...briefingKeys.all, 'detail', catalogId, { refresh }] as const,
};

// QA affordance — `?briefing-error=1` in the URL forces useBriefing's queryFn
// to throw, so E2E tests and manual QA can exercise the error UI (Try-again
// button, retry behavior) without stopping the backend. Replaces the old
// `?dashboard-error=1` param. The param is its own gate; no env check needed.
function isBriefingErrorForced(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('briefing-error') === '1';
}

export function useBriefing(catalogId: string, { refresh = false }: { refresh?: boolean } = {}) {
  return useQuery({
    queryKey: briefingKeys.detail(catalogId, refresh),
    queryFn: () => {
      if (isBriefingErrorForced()) {
        throw new Error('Forced briefing error via ?briefing-error=1');
      }
      return briefingApi.get(catalogId, { refresh });
    },
    enabled: !!catalogId,
    staleTime: 30_000,
    // Explicit default. Briefing cache survives 5 min after the last observer
    // unmounts so a navigate-away-and-back inside that window shows the cached
    // briefing instantly while the staleness check fires in the background.
    gcTime: 5 * 60_000,
    // 404 = catalog unknown / cross-tenant — don't retry. Retry once on other
    // errors so transient blips don't dump the user into the error state
    // immediately; the Try-again button handles longer outages.
    // Forced-error path short-circuits retry so the QA affordance surfaces
    // the error UI immediately, without the ~1s react-query backoff delay.
    retry: (failureCount, error) => {
      if (isBriefingErrorForced()) return false;
      if (isNotFound(error)) return false;
      return failureCount < 1;
    },
  });
}

// New chat session (SC-2251 FE / SC-2250 BE). Creates a fresh ChatSession and
// writes it into the chat-session cache slot AgentTab observes. Side effects
// on success:
//   1. Always wipe the per-catalog agent-chat overlay (local groups, pending
//      text, streaming state, error) and write the returned session into the
//      cache. The user clicked "new chat" — start clean. Cheap, idempotent.
//   2. ONLY when the BE actually created a new session (HTTP 201): fire-and-
//      forget a briefing regen with `?refresh=true`. On a 200 reuse (the BE's
//      rapid-click idempotency guard returned the existing empty session
//      unchanged) we skip the regen entirely — nothing changed, and the
//      narrative LLM runs unconditionally on the `?refresh=true` path
//      server-side, so firing it on a no-op reuse would burn tokens for
//      nothing. The 200 case is reachable in multi-tab (two tabs share the
//      active session; the second tab's "New chat" reuses the first's empty
//      session) even though the no-turns button gate covers the single-tab
//      case — so this status check is the authoritative guard, not the UI.
//   3. Don't `await` the regen inside onSuccess — the BE LLM regen takes
//      seconds, and awaiting would keep the mutation `pending` (and the button
//      disabled) for that whole window even though the chat-session side is
//      done in ~10ms. The fresh briefing lands into the `refresh: false` cache
//      slot (the one AgentTab observes), so the bubble updates the moment the
//      regen completes. On regen failure, fall back to a narrow invalidation.
//
// Replaces the retired 30-min stale-mount heuristic: explicit user intent +
// a genuinely-new session is now the only signal that triggers a BE-side regen.
export function useCreateChatSession(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<CreateChatSessionResult, Error, void>({
    mutationFn: () => createChatSession(catalogId),
    onSuccess: ({ session, created }) => {
      qc.setQueryData(agentChatKeys.session(catalogId), session);
      useAgentChatStore.getState().resetCatalog(catalogId);
      // 200 reuse — the session is unchanged and its briefing is still valid.
      // Skip the expensive narrative regen.
      if (!created) return;
      // Mark briefing-regenerating BEFORE the network call so AgentTab swaps
      // the stale narrative for the "Thinking…" placeholder on the same render
      // tick that the chat panel clears. Without this gate the user sees ~4s
      // of pre-click briefing copy paired with the empty post-click timeline.
      useAppStore.getState().setBriefingRefreshing(catalogId, true);
      void briefingApi
        .get(catalogId, { refresh: true })
        .then(
          (fresh) => qc.setQueryData(briefingKeys.detail(catalogId, false), fresh),
          // Symmetric with the success path: invalidate only this catalog's
          // refresh=false slot rather than `briefingKeys.all`. Otherwise a
          // failed regen during a catalog-switch race would force-refetch
          // unrelated catalogs' briefings.
          () =>
            qc.invalidateQueries({
              queryKey: briefingKeys.detail(catalogId, false),
            }),
        )
        .finally(() => useAppStore.getState().setBriefingRefreshing(catalogId, false));
    },
  });
}

// Live finding counts (SC-2242) — pure live read; no caching on the BE.
// Phase 2 wires per-status polling (5s while analysisStatus.state === 'running').
export const findingCountsKeys = {
  all: ['finding-counts'] as const,
  detail: (catalogId: string) => [...findingCountsKeys.all, 'detail', catalogId] as const,
};

// SC-2247 Phase 5: counts polling during pipeline runs. The BE serves counts
// as a pure live read (no caching), so periodic refetches during a structurer
// run let the user watch findings tick up. Caller passes 5000ms (or whatever
// cadence makes sense) while analysisStatus.state === 'running', undefined
// otherwise — react-query halts the interval when the value is `undefined`.
export function useFindingCounts(
  catalogId: string,
  { pollIntervalMs }: { pollIntervalMs?: number } = {},
) {
  return useQuery({
    queryKey: findingCountsKeys.detail(catalogId),
    queryFn: () => findingCountsApi.get(catalogId),
    enabled: !!catalogId,
    // No staleTime — counts are intentionally always live (every observer
    // mount triggers a refetch). gcTime is the orthogonal concern of "how
    // long to keep data when nobody's watching"; 5 min matches useBriefing
    // and avoids a skeleton flash on tab re-mount within that window —
    // staleTime:0 already guarantees the stale data is refreshed in the
    // background before the user can act on it.
    gcTime: 5 * 60_000,
    refetchInterval: pollIntervalMs,
    retry: (failureCount, error) => {
      if (isNotFound(error)) return false;
      return failureCount < 1;
    },
  });
}

// SC-1871 analysis status — drives the global AnalysisStatusBar and inline
// AnalysisInProgressBanner. Polls every 10s while state is `running`; stops
// once the BE reports `complete` or `failed`. If the endpoint hasn't returned
// data yet (initial load) or errored, callers see a `not_started` shape so
// the UI degrades to "no indicator" rather than a wrong banner.
const ANALYSIS_STATUS_POLL_INTERVAL_MS = 10_000;

const NOT_STARTED_STATUS: AnalysisStatusResponse = {
  state: 'not_started',
  current_phase: null,
  started_at: null,
  completed_at: null,
  failed_phase: null,
  error: null,
};

export const analysisStatusKeys = {
  all: ['analysis-status'] as const,
  detail: (catalogId: string) => [...analysisStatusKeys.all, catalogId] as const,
};

export function useAnalysisStatus(catalogId: string): AnalysisStatusResponse {
  const query = useQuery({
    queryKey: analysisStatusKeys.detail(catalogId),
    queryFn: () => analysisStatusApi.get(catalogId),
    enabled: !!catalogId,
    staleTime: 30_000,
    retry: 1,
    refetchInterval: (q) =>
      q.state.data?.state === 'running' ? ANALYSIS_STATUS_POLL_INTERVAL_MS : false,
  });
  return query.data ?? NOT_STARTED_STATUS;
}

interface UploadVars {
  file: File;
  lockedFields?: string[];
  /**
   * Channels every row of this file joins at ingest (SC-3148). Optional, and
   * absent is the default — membership of nothing is the safe direction.
   *
   * Nothing here is invalidated on success: the destinations registry is
   * unchanged by an upload, and the new catalog's rows aren't in any cache yet.
   */
  destinationSlugs?: string[];
}

export function useUploadCatalog() {
  const qc = useQueryClient();
  return useMutation<CatalogResponse, Error, UploadVars>({
    mutationFn: ({ file, lockedFields, destinationSlugs }) =>
      catalogsApi.upload(file, lockedFields, destinationSlugs),
    onSuccess: (data) => {
      // Optimistically prepend the new catalog to the list cache so subsequent
      // navigations (e.g. /welcome → /table via the upload-success redirect)
      // see the new catalog immediately. Without this, EntitiesPage hits the
      // stale empty list cache while the invalidation refetch is in flight,
      // sees no active catalog, and bounces back to /welcome — a loop the user
      // can't escape until the next render cycle catches up.
      qc.setQueryData<CatalogResponse[]>(catalogKeys.list(), (old) =>
        old ? [data, ...old.filter((c) => c.id !== data.id)] : [data],
      );
      qc.setQueryData(catalogKeys.detail(data.id), data);
      qc.invalidateQueries({ queryKey: catalogKeys.list() });
      // Make the just-uploaded catalog active so every surface (sidebar card,
      // status bar, agent tab, redirects) re-points at it immediately — the
      // user clearly cares about the thing they just uploaded.
      useAppStore.getState().setActiveCatalogId(data.id);
    },
  });
}

interface RenameVars {
  id: string;
  name: string;
}

interface RenameContext {
  previousList: CatalogResponse[] | undefined;
  previousDetail: CatalogResponse | undefined;
}

export function useRenameCatalog() {
  const qc = useQueryClient();
  return useMutation<CatalogResponse, Error, RenameVars, RenameContext>({
    mutationFn: ({ id, name }) => catalogsApi.rename(id, name),
    onMutate: async ({ id, name }) => {
      await qc.cancelQueries({ queryKey: catalogKeys.list() });
      await qc.cancelQueries({ queryKey: catalogKeys.detail(id) });
      const previousList = qc.getQueryData<CatalogResponse[]>(catalogKeys.list());
      const previousDetail = qc.getQueryData<CatalogResponse>(catalogKeys.detail(id));
      qc.setQueryData<CatalogResponse[] | undefined>(catalogKeys.list(), (old) =>
        old?.map((c) => (c.id === id ? { ...c, name } : c)),
      );
      if (previousDetail) {
        qc.setQueryData<CatalogResponse>(catalogKeys.detail(id), { ...previousDetail, name });
      }
      return { previousList, previousDetail };
    },
    onSuccess: (data) => {
      // Replace the optimistic value with the BE's authoritative response so
      // server-side state (updated_at, any normalization the BE applies to
      // name) is reflected in the cache.
      qc.setQueryData<CatalogResponse[] | undefined>(catalogKeys.list(), (old) =>
        old?.map((c) => (c.id === data.id ? data : c)),
      );
      qc.setQueryData(catalogKeys.detail(data.id), data);
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previousList) qc.setQueryData(catalogKeys.list(), ctx.previousList);
      if (ctx?.previousDetail) qc.setQueryData(catalogKeys.detail(vars.id), ctx.previousDetail);
    },
  });
}

// --- Team invitations (SC-2795) ---------------------------------------------
// The org owner's own-org invite surface. Org is implicit in the session, so
// the key doesn't scope by org id — it's naturally cleared on auth transition
// with the rest of the RQ cache (SC-2461).
export const invitationKeys = {
  all: ['invitations'] as const,
  list: (page: number, limit: number) =>
    [...invitationKeys.all, 'list', page, limit] as const,
};

export function useInvitations(page = 1, limit = 20) {
  return useQuery({
    queryKey: invitationKeys.list(page, limit),
    queryFn: () => invitationsApi.list(page, limit),
    // Hold the prior page on screen during refetch so paging doesn't flash.
    placeholderData: (prev) => prev,
    // Pending invites drift as teammates accept; keep the window short but
    // non-zero so a create/revoke round-trip doesn't double-fetch.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    // Surface unreachable-backend errors within ~3s, not the default ~30s.
    retry: 1,
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation<CreateInvitationResponse, Error, InviteBody>({
    mutationFn: (body) => invitationsApi.create(body),
    // A new invite changes every page of the list; invalidate the whole tree.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation<Invitation, Error, string>({
    mutationFn: (invitationId) => invitationsApi.revoke(invitationId),
    // Revoke drops the invite from the PENDING list; refetch to reflect it.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitationKeys.all });
    },
  });
}

// --- Team roster (SC-3227) ---------------------------------------------------
// Every active member reads it. Same org-implicit keying as the invitations
// above — cleared with the rest of the RQ cache on auth transition (SC-2461).
export const teamMemberKeys = {
  all: ['team-members'] as const,
  list: () => [...teamMemberKeys.all, 'list'] as const,
};

export function useTeamMembers() {
  return useQuery({
    queryKey: teamMemberKeys.list(),
    queryFn: () => teamMembersApi.list(),
    // Same window as the pending-invites list beside it, so an accepted invite
    // leaves that list and joins this one on the same refetch (mount / focus).
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}

export function useSetMemberTitle() {
  const qc = useQueryClient();
  return useMutation<TeamMember, Error, { membershipId: string; title: string | null }>({
    mutationFn: ({ membershipId, title }) => teamMembersApi.setTitle(membershipId, title),
    // The response IS the updated roster row — swap it in at once. A roster
    // read already in flight (a stale-on-mount or focus refetch, started before
    // or during the save) may hold the PRE-save title and would land on top,
    // making the edit look undone. The invalidate cancels that read
    // (`cancelRefetch` defaults on) and re-reads after the write.
    onSuccess: (row) => {
      qc.setQueryData<TeamMembersResponse>(teamMemberKeys.list(), (prev) =>
        prev && {
          members: prev.members.map((m) => (m.membership_id === row.membership_id ? row : m)),
        },
      );
      void qc.invalidateQueries({ queryKey: teamMemberKeys.list() });
    },
  });
}

// --- Boards (SC-2872) — spec reads + viewer-scoped binds -----------------------

export const boardKeys = {
  all: ['boards'] as const,
  list: (catalogId: string) => [...boardKeys.all, 'list', catalogId] as const,
  detail: (catalogId: string, boardId: string) =>
    [...boardKeys.all, 'detail', catalogId, boardId] as const,
  bind: (catalogId: string, boardId: string, params: Record<string, string>) =>
    [...boardKeys.all, 'bind', catalogId, boardId, params] as const,
};

/** One board's record (title + validated spec). Specs change only through an
 *  agent re-draft, so a short-stale window is plenty. */
export function useBoard(catalogId: string, boardId: string) {
  return useQuery({
    queryKey: boardKeys.detail(catalogId, boardId),
    queryFn: () => boardsApi.get(catalogId, boardId),
    enabled: !!catalogId && !!boardId,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

/**
 * Bind (run) a board's queries under the viewer's own scope. Boards refresh
 * on-view (the board header promises it), so bound data is stale immediately —
 * every mount refetches — while `gcTime` keeps the previous envelope around so a
 * revisit paints instantly under the refetch. `params` are the viewer's tile-
 * control selections (part of the key: each selection is its own bind);
 * `placeholderData` keeps the previous envelope on screen across a param
 * toggle, so only the affected tiles visibly change (no board-wide flash).
 */
export function useBoardBind(
  catalogId: string,
  boardId: string,
  params: Record<string, string>,
) {
  return useQuery({
    queryKey: boardKeys.bind(catalogId, boardId, params),
    queryFn: () => boardsApi.bind(catalogId, boardId, { params }),
    enabled: !!catalogId && !!boardId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: 1,
    placeholderData: (prev) => prev,
  });
}

/** All boards available to this catalog's library (SC-2873) — the shelf list.
 *  Read-mostly; a create (agent authoring, SC-2874) or delete invalidates. */
export function useBoards(catalogId: string) {
  return useQuery({
    queryKey: boardKeys.list(catalogId),
    queryFn: () => boardsApi.list(catalogId),
    enabled: !!catalogId,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

/** Delete a created board (confirm-gated in the Library). Invalidates the
 *  whole boards tree — the list, and the detail/bind of the deleted id. */
export function useDeleteBoard(catalogId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (boardId) => boardsApi.remove(catalogId, boardId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: boardKeys.all });
    },
  });
}

// --- Staff support grants (SC-2948) -----------------------------------------

export const supportGrantKeys = {
  all: ['supportGrants'] as const,
  live: () => [...supportGrantKeys.all, 'live'] as const,
};

export interface MySupportGrants {
  grants: SupportGrant[];
  // The endpoint lists EVERY staff member's grants, one page of up to 100. When
  // the server holds more than that page, the caller's grants may be on a page
  // we didn't read — so say so rather than render a silently short list.
  truncated: boolean;
}

/**
 * The signed-in staff user's support grants — the org switcher's list.
 * `GET /admin/support-access` returns all staff grants, so this filters to
 * `userId`. It does NOT drop expired grants: that depends on the clock at render
 * time, not fetch time, so the rendering surface filters against a ticking `now`
 * (expiry is enforced per request — a still-listed grant past `expires_at` is
 * dead and must never render as enterable). `enabled` gates on staff-ness;
 * non-staff get a 403 from this endpoint and must never call it.
 */
export function useMySupportGrants(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: supportGrantKeys.live(),
    queryFn: () => supportAccessApi.listLive(),
    enabled: enabled && !!userId,
    select: (page): MySupportGrants => ({
      grants: page.items.filter((g) => g.user.id === userId),
      truncated: page.total > page.items.length,
    }),
    // Grants change rarely (a staff member grants themselves one, then works);
    // short enough that a fresh grant shows the next time the menu opens.
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}
